import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Star } from "lucide-react";
import api from "@/lib/api";
import StoryViewer from "@/components/StoryViewer";
import StoryUploadModal from "@/components/StoryUploadModal";

/**
 * Horizontal scrollable rail of story bubbles.
 *
 * Modes:
 *  - global : shows every active story across the platform (used on the directory)
 *  - profile: shows only stories of the given profile (used on ProfileDetail)
 *
 * If `ownerProfile` is provided, the "+" bubble is shown so the owner can
 * publish a new story from the rail.
 *
 * Props:
 *  - mode: 'global' | 'profile'
 *  - profileId: only used when mode='profile'
 *  - ownerProfile: { id, name } — the profile the current user owns and can
 *                  post to (rendered as first "+ Add" bubble). Optional.
 */
const StoryRail = ({ mode = "global", profileId = null, entrepreneurId = null, ownerProfile = null, currentUserId = null, autoOpenStoryId = null }) => {
  const [groups, setGroups] = useState([]); // [{profile_id, profile_name, profile_logo, stories:[]}]
  const [openGroup, setOpenGroup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const autoOpenedRef = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "profile") {
        // Prefer entrepreneurId (aggregates stories across duplicate profile docs
        // that share the same legacy entrepreneur id). Fall back to profileId.
        const url = entrepreneurId
          ? `/stories/entrepreneur/${entrepreneurId}`
          : profileId
            ? `/stories/profile/${profileId}`
            : null;
        if (!url) {
          setGroups([]);
          return;
        }
        const { data } = await api.get(url);
        if ((data.items || []).length) {
          const s = data.items[0];
          setGroups([{
            profile_id: s.profile_id,
            profile_name: s.profile_name,
            profile_logo: s.profile_logo,
            profile_category: s.profile_category,
            profile_kind: s.profile_kind,
            latest_image: s.image_url,
            latest_created_at: s.created_at,
            count: data.items.length,
            stories: data.items,
          }]);
        } else {
          setGroups([]);
        }
      } else {
        const { data } = await api.get("/stories/active", { params: { limit: 40 } });
        setGroups(data.items || []);
      }
    } catch (e) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [mode, profileId, entrepreneurId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open a specific story once when groups load (used by shared links).
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!autoOpenStoryId || !groups.length) return;
    for (const g of groups) {
      const found = (g.stories || []).find((s) => s.id === autoOpenStoryId);
      if (found) {
        // Reorder so the target story is first — viewer starts at idx 0.
        const rest = g.stories.filter((s) => s.id !== autoOpenStoryId);
        setOpenGroup({ ...g, stories: [found, ...rest] });
        autoOpenedRef.current = true;
        break;
      }
    }
  }, [autoOpenStoryId, groups]);

  const handlePublished = () => {
    fetchData();
    setUploading(false);
  };

  const handleDelete = async (storyId) => {
    try {
      await api.delete(`/stories/${storyId}`);
      // Optimistic removal from openGroup
      setOpenGroup((g) => {
        if (!g) return g;
        const remaining = g.stories.filter((s) => s.id !== storyId);
        return remaining.length ? { ...g, stories: remaining } : null;
      });
      fetchData();
    } catch (e) {
      window.alert("No se pudo eliminar la historia.");
    }
  };

  const showAddBubble = !!ownerProfile;
  const hasContent = showAddBubble || groups.length > 0;

  if (!hasContent && !loading) return null;

  return (
    <div className="w-full" data-testid={`story-rail-${mode}`}>
      <div className={`flex items-center gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory ${mode === "profile" ? "mb-6" : ""}`}>
        {showAddBubble && (
          <button
            type="button"
            onClick={() => setUploading(true)}
            className="shrink-0 snap-start flex flex-col items-center gap-1.5"
            data-testid="story-add-bubble"
          >
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-dashed border-orange bg-cream flex items-center justify-center text-orange hover:bg-orange hover:text-white transition-colors">
              <Plus size={22} />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-teal-soft">
              Nueva
            </span>
          </button>
        )}
        {loading && !groups.length ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="shrink-0 flex flex-col items-center gap-1.5 animate-pulse">
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-200" />
              <span className="w-14 h-2 bg-gray-200 rounded" />
            </div>
          ))
        ) : null}
        {groups.map((g) => {
          const hasPinned = g.has_pinned || (g.stories || []).some((s) => s.is_pinned);
          return (
          <button
            key={g.profile_id}
            type="button"
            onClick={() => setOpenGroup(g)}
            className="shrink-0 snap-start flex flex-col items-center gap-1.5 group relative"
            data-testid={`story-bubble-${g.profile_id}`}
          >
            <span className={`p-[3px] rounded-full ${hasPinned ? "bg-gradient-to-tr from-yellow-400 via-orange to-yellow-300" : "bg-gradient-to-tr from-orange via-pink-500 to-yellow-400"}`}>
              <span className="block w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white overflow-hidden bg-teal-mist">
                {g.profile_logo ? (
                  <img
                    src={g.profile_logo}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : g.latest_image ? (
                  <img src={g.latest_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="w-full h-full flex items-center justify-center font-display text-white text-lg bg-teal">
                    {(g.profile_name || "?").slice(0, 1)}
                  </span>
                )}
              </span>
            </span>
            {hasPinned && (
              <span
                className="absolute top-0 right-0 w-5 h-5 rounded-full bg-yellow-400 border-2 border-white shadow flex items-center justify-center"
                aria-label="Historia destacada"
                data-testid={`story-pinned-badge-${g.profile_id}`}
              >
                <Star size={9} className="text-white fill-white" />
              </span>
            )}
            <span className="text-[10px] font-semibold text-teal-deep max-w-[80px] truncate">
              {g.profile_name}
            </span>
          </button>
        );
        })}
      </div>

      {openGroup && (
        <StoryViewer
          group={openGroup}
          onClose={() => setOpenGroup(null)}
          onDelete={handleDelete}
          onPinChanged={(storyId, isPinned) => {
            // Update in-place so the open viewer reflects the new state immediately
            setOpenGroup((g) =>
              g
                ? {
                    ...g,
                    stories: (g.stories || []).map((s) =>
                      s.id === storyId ? { ...s, is_pinned: isPinned } : s
                    ),
                  }
                : g
            );
            fetchData();
          }}
          canDelete={
            !!currentUserId &&
            (openGroup.stories || []).some((s) => s.user_id === currentUserId)
          }
        />
      )}

      {uploading && ownerProfile && (
        <StoryUploadModal
          profileId={ownerProfile.id}
          profileName={ownerProfile.name}
          onClose={() => setUploading(false)}
          onPublished={handlePublished}
        />
      )}
    </div>
  );
};

export default StoryRail;
